import React, { useEffect, useState, useRef } from 'react';
import { API_BASE_URL, authHeaders } from '../../config';
import {
    IonButton, IonCol, IonContent, IonGrid,
    IonInput, IonItem, IonPage, IonRow,
    IonCard, IonCardContent, IonIcon, IonToast, IonSpinner,
    IonSelect, IonSelectOption
} from "@ionic/react";
import { saveOutline, chevronDownOutline, closeCircleOutline } from 'ionicons/icons';
import { useParams, useHistory } from 'react-router-dom';
import { PageHeader, AppBackground } from '../../components/PageHeader';
import '../form/form.css';

// ── Tipos ────────────────────────────────────────────────────────────────────

interface Departamento {
    id: number;
    nombre: string;
}

interface Municipio {
    id: number;
    nombre: string;
    departamento_id: number;
}

interface PresentadorOption {
    id: number;
    nombre_completo: string;
    alias?: string;
    numero_cedula?: string;
}

// ── Selector de Presentador ──────────────────────────────────────────────────

interface PresentadorSelectProps {
    value: PresentadorOption | null;
    onChange: (option: PresentadorOption | null) => void;
    options: PresentadorOption[];
    loading: boolean;
    currentId: number;   // ID del referido actual (para excluirlo de opciones)
}

const PresentadorSelect: React.FC<PresentadorSelectProps> = ({
    value, onChange, options, loading, currentId
}) => {
    const [inputText, setInputText] = useState(value ? value.nombre_completo : '');
    const [open, setOpen] = useState(false);
    const [filtered, setFiltered] = useState<PresentadorOption[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);

    const availableOptions = options.filter(o => o.id !== currentId);

    useEffect(() => {
        setInputText(value ? value.nombre_completo : '');
    }, [value]);

    useEffect(() => {
        const q = inputText.trim().toLowerCase();
        if (q === '') {
            setFiltered(availableOptions);
        } else {
            setFiltered(availableOptions.filter(o =>
                o.nombre_completo.toLowerCase().includes(q) ||
                (o.alias && o.alias.toLowerCase().includes(q)) ||
                (o.numero_cedula && o.numero_cedula.includes(q))
            ));
        }
    }, [inputText, options]);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
                setInputText(value ? value.nombre_completo : '');
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [value]);

    const handleSelect = (opt: PresentadorOption) => {
        setInputText(opt.nombre_completo);
        onChange(opt);
        setOpen(false);
    };

    const handleClear = () => {
        setInputText('');
        onChange(null);
        setOpen(false);
    };

    return (
        <div className="creatable-select-wrapper" ref={containerRef}>
            <label className="creatable-select-label">Referido por</label>
            <div className={`creatable-select-control ${open ? 'open' : ''}`}>
                <input
                    className="creatable-select-input"
                    type="text"
                    value={inputText}
                    placeholder="Buscar por nombre, alias o cédula..."
                    onFocus={() => setOpen(true)}
                    onChange={e => { setInputText(e.target.value); setOpen(true); }}
                />
                <div className="creatable-select-icons">
                    {loading && <IonSpinner name="crescent" className="select-spinner" />}
                    {inputText && !loading && (
                        <button type="button" className="creatable-clear-btn" onClick={handleClear} tabIndex={-1}>
                            <IonIcon icon={closeCircleOutline} />
                        </button>
                    )}
                    {!loading && (
                        <IonIcon
                            icon={chevronDownOutline}
                            className={`creatable-chevron ${open ? 'rotated' : ''}`}
                            onClick={() => setOpen(v => !v)}
                        />
                    )}
                </div>
            </div>

            {open && (
                <div className="creatable-select-dropdown">
                    <div
                        className={`creatable-option ${!value ? 'selected' : ''}`}
                        onMouseDown={() => { onChange(null); setInputText(''); setOpen(false); }}
                    >
                        <em style={{ opacity: 0.6 }}>Sin presentador (referido directo)</em>
                    </div>
                    {filtered.length > 0 ? (
                        filtered.map(opt => (
                            <div
                                key={opt.id}
                                className={`creatable-option ${value && value.id === opt.id ? 'selected' : ''}`}
                                onMouseDown={() => handleSelect(opt)}
                            >
                                <strong>{opt.nombre_completo}</strong>
                                {opt.alias && (
                                    <span style={{ marginLeft: 6, opacity: 0.75 }}>“{opt.alias}”</span>
                                )}
                                {opt.numero_cedula && (
                                    <span style={{ marginLeft: 8, opacity: 0.6, fontSize: '0.85em' }}>
                                        C.C. {opt.numero_cedula}
                                    </span>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="creatable-option creatable-option--empty">Sin resultados</div>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Componente Principal ─────────────────────────────────────────────────────

const EditarReferido: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const numericId = parseInt(id, 10);

    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [saving, setSaving] = useState(false);

    // Datos del formulario
    const [formData, setFormData] = useState({
        numero_cedula: '',
        nombre_completo: '',
        alias: '',
        telefono: '',
        genero: '',
        lugar_de_votacion: '',
        mesa_de_votacion: '',
        municipio_id: null as number | null,
        direccion_de_residencia: '',
        presentador_id: null as number | null,
    });

    // Datos de ubicación
    const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
    const [municipios, setMunicipios] = useState<Municipio[]>([]);
    const [loadingMunicipios, setLoadingMunicipios] = useState(false);
    const [selectedDeptId, setSelectedDeptId] = useState<number | null>(null);

    // Opciones de presentador
    const [presentadorOptions, setPresentadorOptions] = useState<PresentadorOption[]>([]);
    const [loadingPresentador, setLoadingPresentador] = useState(true);
    const [selectedPresentador, setSelectedPresentador] = useState<PresentadorOption | null>(null);

    // ── Carga inicial ──

    useEffect(() => {
        fetchReferido();
        fetchDepartamentos();
        fetchPresentadorOptions();
    }, [id]);

    const fetchReferido = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/referidos/${id}`, { headers: authHeaders() });
            const result = await response.json();
            if (result.status === 'success') {
                const d = result.data;
                setFormData({
                    numero_cedula: d.numero_cedula || '',
                    nombre_completo: d.nombre_completo || '',
                    alias: d.alias || '',
                    telefono: d.telefono || '',
                    genero: d.genero || '',
                    lugar_de_votacion: d.lugar_de_votacion || '',
                    mesa_de_votacion: d.mesa_de_votacion || '',
                    municipio_id: d.municipio_id || null,
                    direccion_de_residencia: d.direccion_de_residencia || '',
                    presentador_id: d.presentador_id || null,
                });

                // Si tiene departamento, cargar municipios
                if (d.departamento_id) {
                    setSelectedDeptId(d.departamento_id);
                    fetchMunicipiosByDept(d.departamento_id);
                }
            }
        } catch (error) {
            console.error('Error fetching referido:', error);
        }
    };

    const fetchDepartamentos = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/departamentos`);
            const result = await res.json();
            if (result.status === 'success') {
                setDepartamentos(result.data);
            }
        } catch (err) {
            console.error('Error cargando departamentos:', err);
        }
    };

    const fetchMunicipiosByDept = async (deptId: number) => {
        setLoadingMunicipios(true);
        try {
            const res = await fetch(`${API_BASE_URL}/municipios/${deptId}`);
            const result = await res.json();
            if (result.status === 'success') {
                setMunicipios(result.data);
            }
        } catch (err) {
            console.error('Error cargando municipios:', err);
        } finally {
            setLoadingMunicipios(false);
        }
    };

    const fetchPresentadorOptions = async () => {
        setLoadingPresentador(true);
        try {
            const res = await fetch(`${API_BASE_URL}/referidos-options`, { headers: authHeaders() });
            const result = await res.json();
            if (result.status === 'success') {
                setPresentadorOptions(result.data);
            }
        } catch (err) {
            console.error('Error cargando presentadores:', err);
        } finally {
            setLoadingPresentador(false);
        }
    };

    // Sincronizar presentador seleccionado cuando se cargan opciones o datos del referido
    useEffect(() => {
        if (formData.presentador_id && presentadorOptions.length > 0) {
            const found = presentadorOptions.find(o => o.id === formData.presentador_id);
            setSelectedPresentador(found || null);
        }
    }, [formData.presentador_id, presentadorOptions]);

    // ── Handlers ──

    const handleInputChange = (name: string, value: any) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDepartamentoChange = (deptId: number | null) => {
        setSelectedDeptId(deptId);
        setMunicipios([]);
        handleInputChange('municipio_id', null);
        if (deptId) {
            fetchMunicipiosByDept(deptId);
        }
    };

    const handlePresentadorChange = (opt: PresentadorOption | null) => {
        setSelectedPresentador(opt);
        handleInputChange('presentador_id', opt ? opt.id : null);
    };

    const handleUpdate = async () => {
        if (!formData.nombre_completo.trim()) {
            setToastMessage('El nombre completo es obligatorio');
            setShowToast(true);
            return;
        }
        setSaving(true);
        try {
            const response = await fetch(`${API_BASE_URL}/referidos/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(formData)
            });
            const result = await response.json();
            if (result.status === 'success') {
                setToastMessage('Referido actualizado exitosamente');
                setShowToast(true);
                setTimeout(() => history.push('/ver-referidos'), 2000);
            } else {
                setToastMessage('Error: ' + (result.message || 'No se pudo actualizar'));
                setShowToast(true);
            }
        } catch (error) {
            setToastMessage('Error de conexión');
            setShowToast(true);
        } finally {
            setSaving(false);
        }
    };

    const textFields = [
        { label: 'Nombre Completo *', name: 'nombre_completo', placeholder: 'Ingrese el nombre', size: "7" },
        { label: 'Alias / apodo', name: 'alias', placeholder: 'Cómo le dicen', size: "5" },
        { label: 'Cédula', name: 'numero_cedula', placeholder: 'Número de identificación', size: "6" },
        { label: 'Teléfono', name: 'telefono', placeholder: 'Número de contacto', size: "6", type: 'tel' },
        { label: 'Género', name: 'genero', placeholder: 'Género', size: "8" },
        { label: 'Dirección', name: 'direccion_de_residencia', placeholder: 'Dirección de residencia', size: "12" },
        { label: 'Lugar de Votación', name: 'lugar_de_votacion', placeholder: 'Lugar', size: "8" },
        { label: 'Mesa de Votación', name: 'mesa_de_votacion', placeholder: 'Mesa', size: "4" },
    ];

    return (
        <IonPage className="app-page">
            <AppBackground />

            <PageHeader title="Editar Referido" subtitle="Actualice la información del referido" backHref="/ver-referidos" />

            <IonContent className="content-transparent ion-padding-horizontal">
                <div className="app-container">
                            <IonCard className="app-card form-card">
                                <IonCardContent>
                                    <IonGrid>
                                        <IonRow>
                                            {/* Campos de texto */}
                                            {textFields.map((field) => {
                                                if (field.name === 'genero') {
                                                    return (
                                                        <IonCol size="12" sizeMd={field.size} key={field.name}>
                                                            <div className="gender-selector-container">
                                                                <label className="gender-selector-label">Género</label>
                                                                <div className="gender-options">
                                                                    <div
                                                                        className={`gender-option-card ${formData.genero === 'Hombre' ? 'selected' : ''}`}
                                                                        onClick={() => handleInputChange('genero', 'Hombre')}
                                                                    >
                                                                        <div className="gender-checkbox-circle">
                                                                            <div className="gender-checkbox-inner" />
                                                                        </div>
                                                                        <span>Hombre</span>
                                                                    </div>
                                                                    <div
                                                                        className={`gender-option-card ${formData.genero === 'Mujer' ? 'selected' : ''}`}
                                                                        onClick={() => handleInputChange('genero', 'Mujer')}
                                                                    >
                                                                        <div className="gender-checkbox-circle">
                                                                            <div className="gender-checkbox-inner" />
                                                                        </div>
                                                                        <span>Mujer</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </IonCol>
                                                    );
                                                }
                                                return (
                                                    <IonCol size="12" sizeMd={field.size} key={field.name}>
                                                        <IonItem className="custom-input-item" lines="none">
                                                            <IonInput
                                                                label={field.label}
                                                                labelPlacement="stacked"
                                                                name={field.name}
                                                                type={field.type as any || 'text'}
                                                                placeholder={field.placeholder}
                                                                value={(formData as any)[field.name]}
                                                                onIonInput={(e: any) => handleInputChange(field.name, e.detail.value)}
                                                            />
                                                        </IonItem>
                                                    </IonCol>
                                                );
                                            })}

                                            {/* Departamento */}
                                            <IonCol size="12" sizeMd="6">
                                                <IonItem className="custom-input-item" lines="none">
                                                    <IonSelect
                                                        label="Departamento"
                                                        labelPlacement="stacked"
                                                        placeholder="Seleccione departamento"
                                                        value={selectedDeptId}
                                                        onIonChange={(e: any) => handleDepartamentoChange(e.detail.value)}
                                                    >
                                                        {departamentos.map(d => (
                                                            <IonSelectOption key={d.id} value={d.id}>
                                                                {d.nombre}
                                                            </IonSelectOption>
                                                        ))}
                                                    </IonSelect>
                                                </IonItem>
                                            </IonCol>

                                            {/* Municipio */}
                                            <IonCol size="12" sizeMd="6">
                                                <IonItem className="custom-input-item" lines="none">
                                                    <IonSelect
                                                        label="Municipio"
                                                        labelPlacement="stacked"
                                                        placeholder={
                                                            loadingMunicipios ? "Cargando..." :
                                                                selectedDeptId ? "Seleccione municipio" :
                                                                    "Primero seleccione departamento"
                                                        }
                                                        value={formData.municipio_id}
                                                        disabled={!selectedDeptId || loadingMunicipios}
                                                        onIonChange={(e: any) => handleInputChange('municipio_id', e.detail.value)}
                                                    >
                                                        {municipios.map(m => (
                                                            <IonSelectOption key={m.id} value={m.id}>
                                                                {m.nombre}
                                                            </IonSelectOption>
                                                        ))}
                                                    </IonSelect>
                                                </IonItem>
                                            </IonCol>

                                            {/* Selector de Presentador */}
                                            <IonCol size="12">
                                                <div style={{ marginBottom: '12px' }}>
                                                    <PresentadorSelect
                                                        value={selectedPresentador}
                                                        onChange={handlePresentadorChange}
                                                        options={presentadorOptions}
                                                        loading={loadingPresentador}
                                                        currentId={numericId}
                                                    />
                                                </div>
                                            </IonCol>

                                            {/* Botón guardar */}
                                            <IonCol size="12" className="ion-margin-top">
                                                <IonButton
                                                    expand="block"
                                                    onClick={handleUpdate}
                                                    className="app-btn-primary"
                                                    shape="round"
                                                    disabled={saving}
                                                >
                                                    {saving
                                                        ? <IonSpinner name="crescent" />
                                                        : <><IonIcon icon={saveOutline} slot="start" />Guardar Cambios</>
                                                    }
                                                </IonButton>
                                            </IonCol>
                                        </IonRow>
                                    </IonGrid>
                                </IonCardContent>
                            </IonCard>
                </div>

                <IonToast
                    isOpen={showToast}
                    onDidDismiss={() => setShowToast(false)}
                    message={toastMessage}
                    duration={2000}
                    position="bottom"
                    className="custom-toast"
                />
            </IonContent>
        </IonPage>
    );
};

export default EditarReferido;
